import { CatalogCard, ChatMessage } from '../Components/Shared/API/ApiTypes';

// --- Analytics ---

export enum EventTypes {
    PageVisit = "PageVisit",
    ChatOpened = "ChatOpened",
    InformationEntered = "InformationEntered",
    QuestionAsked = "QuestionAsked",
    SearchMade = "SearchMade",
    /**
     * Conversational forms. Single-emitter rule (platform amendment 2026-07-31):
     * each form event has exactly one producer. The widget owns `FormStarted`
     * because only it observes a tap on the start card; every write-anchored
     * event (`FieldFilledViaChat`, `FormSubmitted`, `FormDiscarded`,
     * `FormUpdated`) is emitted by Bot.API at the write, so they are absent here
     * deliberately — emitting them client-side double-counted every chat fill.
     */
    FormStarted = "FormStarted"
}

export interface LogEvent {
    eventtype: string | null;
    eventvalue: string | null;
    indexname: string | null;
    pageurl: string | null;
    sessionid: string | null;
}

export interface LogEventResponse {
    success: boolean;
}

// --- Search / Q&A ---

export interface PageResult {
    url: string;
    title: string;
}

export interface Answer {
    answer: string;
    links: PageResult[];
    cards?: CatalogCard[];
    /**
     * Conversational forms: the fields this turn extracted or saved. Design D4 —
     * the UI renders fills exclusively from this payload, never from optimistic
     * client inference, so what the student sees is what the backend recorded.
     * Absent on every turn that filled nothing (and on backends predating it).
     */
    fills?: FormFill[];
    /**
     * The form the bot is offering on this turn, by `objectid`, or null/absent.
     * Derived server-side from askability state — never from the model's prose —
     * and never signalled for a started, submitted, or dismissed form
     * (`forms-coordination`). Drives the inline start card.
     */
    form_offer?: string | null;
    /**
     * Present when extraction failed on every layer or ran out of its budget.
     * The answer still went out and nothing was written, so the identical
     * message can be re-run. A turn can therefore fail while the HTTP call
     * succeeds — this is the second failure path, not an alternative to it.
     */
    extraction_error?: ExtractionError;
}

// --- Conversational forms ---

export interface ExtractionError {
    code: string;
    retryable: boolean;
}

/**
 * One field extracted or saved during a turn. `field_value` is omitted for
 * `sensitive` fields — the receipt names the field but the value never enters
 * the chat history.
 */
export interface FormFill {
    field_id: string;
    label?: string;
    field_value?: string | string[] | null;
    sensitive?: boolean;
}

/** A field the widget considers open for this turn. The open-field list is
 *  client state; the server never re-derives it. */
export interface OpenFieldSpec {
    id: string;
    type: string;
    label: string;
    options: Array<{ value: string; label: string }>;
    optional: boolean;
    sensitive: boolean;
    noAI: boolean;
    help?: string;
}

/** Query parameters for the collected-values read (a GET, not a post). */
export interface CollectedValuesRequest {
    sessionid: string;
    index: string;
    objectid: string;
}

export interface CollectedFieldValue {
    field_id: string;
    value: string | string[] | null;
    /** `confirmed` = entered or corrected in the form view; `filled` = committed
     *  from a conversational turn. Provenance only — both count as committed. */
    status?: string;
    authority?: string;
    updated_at?: number;
}

export interface CollectedValuesResponse {
    sessionid: string;
    index: string;
    objectid: string;
    /** The merged view only — the per-authority maps never leave the server. */
    fields: CollectedFieldValue[];
    /** Field count of the form definition. The widget computes its own
     *  percentage from the definition so conditional visibility is respected. */
    total_fields?: number | null;
    /** This form was already submitted (store tracker 6.20). The widget imports
     *  the values (they serve future corrections) but never re-pins the form as
     *  in progress. Optional: an older backend omits it, defaulting false. */
    submitted?: boolean;
    /** This form was dismissed (store tracker 6.20). The server sends an empty
     *  `fields` list alongside; the widget treats it exactly like a local
     *  discard — nothing imported, nothing pinned. Optional, defaults false. */
    dismissed?: boolean;
}

/** Mirrors the proactive-dismiss shape (ADR #55). */
export interface FormDismissRequest {
    sessionid: string;
    index: string;
    objectid: string;
    kind?: 'explicit' | 'implicit';
    pageurl?: string | null;
}

export interface FormDismissResponse {
    dismissed: boolean;
    objectid: string;
    /** False when this objectid was already dismissed — the call is a no-op. */
    first?: boolean;
}

// --- Catalog ---

export interface CatalogRequest {
    question: string;
    index: string;
    sessionid: string;
    personal_details: any;
    context: ChatMessage[];
}

export interface CatalogResponse {
    response: any;
    personal_details: any;
    filtered_items: any;
    form_details: any;
    links?: PageResult[];
    cards?: CatalogCard[];
}

// --- Proactive messaging ---

export interface ProactiveRequest {
    sessionid: string;
    index: string;
    proactiveAttemptId: string;
    context?: ChatMessage[];
    current_url?: string;
}

export interface ProactiveResponse {
    send: boolean;
    proactiveMessageId?: string;
    answer?: string;
    links?: PageResult[];
    displayMode?: string;
    nextDelaySeconds?: number;
    reason?: string;
}

export interface DismissRequest {
    sessionid: string;
    index: string;
    proactiveMessageId?: string;
}

export interface DismissResponse {
    dismissed: boolean;
}
